/**
 * Developer Dashboard Page
 *
 * Shows developer's projects and reservations.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchMyProjects } from '@/features/projects/api/projectsApi'
import { fetchProjectReservations } from '@/features/reservations/api/reservationsApi'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

export default function DeveloperDashboardPage() {
  const [activeTab, setActiveTab] = useState('projects')
  const [selectedProject, setSelectedProject] = useState(null)
  const [reservationFilter, setReservationFilter] = useState('active')
  const { user } = useAuth()

  // Fetch developer's projects
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['my-projects'],
    queryFn: () => fetchMyProjects(),
  })

  // Fetch reservations for all projects with ASP status
  const { data: reservationsData, isLoading: reservationsLoading } = useQuery({
    queryKey: ['project-reservations'],
    queryFn: async () => {
      const result = await fetchProjectReservations()
      if (result.error) return result

      // Fetch ASP status for each reservation
      const reservationsWithASP = await Promise.all(
        (result.data || []).map(async (reservation) => {
          const { data: asp } = await supabase
            .from('sale_purchase_agreements')
            .select('id, status, buyer_signed_at, developer_signed_at, created_at')
            .eq('reservation_id', reservation.id)
            .maybeSingle()

          return {
            ...reservation,
            asp: asp || null,
          }
        })
      )

      return { data: reservationsWithASP, error: null }
    },
  })

  // Fetch unanswered questions per project
  const { data: unansweredQuestionsData } = useQuery({
    queryKey: ['unanswered-questions', user?.id],
    queryFn: async () => {
      const projects = projectsData?.data || []
      const counts = {}

      for (const project of projects) {
        const { count, error } = await supabase
          .from('project_questions')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', project.id)
          .is('answer', null)

        if (!error) {
          counts[project.id] = count || 0
        }
      }

      return counts
    },
    enabled: !!projectsData?.data?.length,
  })

  // Fetch unread messages per project
  const { data: unreadMessagesData } = useQuery({
    queryKey: ['unread-messages', user?.id],
    queryFn: async () => {
      const projects = projectsData?.data || []
      const counts = {}

      for (const project of projects) {
        const { data: conversations, error: convError } = await supabase
          .from('project_conversations')
          .select('id, project_id, project:project_id(developer_id)')
          .eq('project_id', project.id)

        if (!convError && conversations) {
          let totalUnread = 0

          for (const conv of conversations) {
            const { count, error } = await supabase
              .from('conversation_messages')
              .select('*, sender:sender_id(role)', { count: 'exact', head: true })
              .eq('conversation_id', conv.id)
              .is('read_at', null)

            if (!error && count) {
              const { data: messages } = await supabase
                .from('conversation_messages')
                .select('*, sender:sender_id(role)')
                .eq('conversation_id', conv.id)
                .is('read_at', null)

              const unreadFromBuyers = messages?.filter(m => m.sender?.role === 'buyer').length || 0
              totalUnread += unreadFromBuyers
            }
          }

          counts[project.id] = totalUnread
        }
      }

      return counts
    },
    enabled: !!projectsData?.data?.length,
  })

  const projects = projectsData?.data || []
  const reservations = reservationsData?.data || []
  const activeReservations = reservations.filter(r => r.status === 'active')
  const filteredReservations = reservationFilter === 'active'
    ? activeReservations
    : reservations
  const unansweredQuestions = unansweredQuestionsData || {}
  const unreadMessages = unreadMessagesData || {}

  const getProjectStats = (projectId) => {
    const projectReservations = reservations.filter(
      r => r.unit?.project?.id === projectId
    )
    return {
      total: projectReservations.length,
      active: projectReservations.filter(r => r.status === 'active').length,
      converted: projectReservations.filter(r => r.status === 'converted').length,
    }
  }

  const getContractStatus = (reservation) => {
    if (!reservation.asp) {
      return {
        label: 'Not Started',
        color: 'bg-secondary-100 text-secondary-800',
        description: 'Reservation active, contract not yet initiated',
        nextStep: 'Waiting for buyer to initiate contract process',
        icon: '⏸️',
      }
    }

    const status = reservation.asp.status

    switch (status) {
      case 'draft':
        return {
          label: 'Draft',
          color: 'bg-blue-100 text-blue-800',
          description: 'Contract generated, buyer reviewing',
          nextStep: 'Waiting for buyer to review and proceed',
          icon: '📝',
        }
      case 'sent_to_lawyer':
        return {
          label: 'With Lawyer',
          color: 'bg-purple-100 text-purple-800',
          description: 'Buyer has shared with their lawyer',
          nextStep: 'Waiting for buyer to complete lawyer review',
          icon: '⚖️',
        }
      case 'ready_to_sign':
        return {
          label: 'Ready to Sign',
          color: 'bg-amber-100 text-amber-800',
          description: 'Buyer is ready to sign',
          nextStep: 'Waiting for buyer signature',
          icon: '✍️',
        }
      case 'signed_by_buyer':
        return {
          label: 'Awaiting Your Signature',
          color: 'bg-orange-100 text-orange-800',
          description: 'Buyer has signed, waiting for developer',
          nextStep: 'Review and counter-sign the agreement',
          icon: '🔔',
        }
      case 'signed_by_developer':
      case 'fully_executed':
        return {
          label: 'Fully Executed',
          color: 'bg-green-100 text-green-800',
          description: 'Contract signed by both parties',
          nextStep: 'Contract complete - proceed with settlement',
          icon: '✅',
        }
      case 'withdrawn':
        return {
          label: 'Withdrawn',
          color: 'bg-red-100 text-red-800',
          description: 'Contract withdrawn by buyer',
          nextStep: 'Contract cancelled',
          icon: '❌',
        }
      case 'lapsed':
        return {
          label: 'Lapsed',
          color: 'bg-gray-100 text-gray-800',
          description: 'Contract expired',
          nextStep: 'Contract expired without completion',
          icon: '⏱️',
        }
      default:
        return {
          label: status,
          color: 'bg-secondary-100 text-secondary-800',
          description: 'Status unknown',
          nextStep: 'Check contract details',
          icon: '❓',
        }
    }
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-2">
              Developer Dashboard
            </h1>
            <p className="text-lg text-secondary-600">
              Manage your projects and reservations
            </p>
          </div>
          <Link to="/developer/projects/create" className="btn-primary px-6 py-3">
            Create Project
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Total Projects</div>
            <div className="text-3xl font-bold text-secondary-900">{projects.length}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Active Reservations</div>
            <div className="text-3xl font-bold text-success-600">{activeReservations.length}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Total Reservations</div>
            <div className="text-3xl font-bold text-primary-600">{reservations.length}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-secondary-600 mb-1">Converted</div>
            <div className="text-3xl font-bold text-success-600">
              {reservations.filter(r => r.status === 'converted').length}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-secondary-200">
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'projects'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            My Projects
            {projects.length > 0 && (
              <span className="ml-2 badge badge-primary">{projects.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reservations')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === 'reservations'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Contracted
            {activeReservations.length > 0 && (
              <span className="ml-2 badge badge-success">{activeReservations.length}</span>
            )}
          </button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div>
            {projectsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner spinner-lg text-primary-600"></div>
              </div>
            ) : projects.length === 0 ? (
              <div className="card p-12">
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">No projects yet</h3>
                  <p className="empty-state-description">
                    Create your first project to start receiving reservations from buyers.
                  </p>
                  <Link to="/developer/projects/create" className="btn-primary mt-4 inline-block">
                    Create Project
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Create Project Card */}
                <Link
                  to="/developer/projects/create"
                  className="card overflow-hidden group hover:shadow-lg transition-all duration-300 border-2 border-dashed border-secondary-300 hover:border-primary-500"
                >
                  <div className="aspect-video bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center group-hover:from-primary-100 group-hover:to-primary-200 transition-all duration-300">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-md mb-4 group-hover:scale-110 transition-transform duration-300">
                        <svg
                          className="w-8 h-8 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-700 transition-colors">
                      Create New Project
                    </h3>
                    <p className="text-sm text-secondary-600 mb-4">
                      Start a new development project
                    </p>
                    <div className="btn-primary w-full">
                      Get Started
                    </div>
                  </div>
                </Link>

                {projects.map((project) => {
                  const unansweredCount = unansweredQuestions[project.id] || 0
                  const unreadCount = unreadMessages[project.id] || 0
                  const hasAlerts = unansweredCount > 0 || unreadCount > 0

                  return (
                    <Link
                      key={project.id}
                      to={`/developer/projects/${project.id}/edit`}
                      className="card overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                    >
                      <div className="aspect-video bg-secondary-200 overflow-hidden">
                        {project.image_urls?.[0] ? (
                          <img
                            src={project.image_urls[0]}
                            alt={project.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-12 h-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-secondary-900 hover:text-primary-600">
                            {project.name}
                          </h3>
                          <span className={`badge ${
                            project.status === 'activated' ? 'badge-success' :
                            project.status === 'coming_soon' ? 'badge-warning' :
                            project.status === 'draft' ? 'badge-secondary' :
                            'badge-primary'
                          }`}>
                            {project.status === 'coming_soon' ? 'Coming Soon' : project.status}
                          </span>
                        </div>

                        <p className="text-sm text-secondary-600 mb-4">{project.location}</p>

                        {hasAlerts && (
                          <div className="flex items-center gap-3 pt-3 border-t border-secondary-200">
                            {unansweredCount > 0 && (
                              <div className="flex items-center gap-1.5">
                                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-amber-100 text-amber-900 text-xs font-bold rounded-full">
                                  {unansweredCount}
                                </span>
                              </div>
                            )}
                            {unreadCount > 0 && (
                              <div className="flex items-center gap-1.5">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-blue-100 text-blue-900 text-xs font-bold rounded-full">
                                  {unreadCount}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === 'reservations' && (
          <div>
            {/* Filter Buttons */}
            {reservations.length > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <button
                  onClick={() => setReservationFilter('active')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    reservationFilter === 'active'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-secondary-700 hover:bg-secondary-50 border border-secondary-200'
                  }`}
                >
                  Active
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-white/20">
                    {activeReservations.length}
                  </span>
                </button>
                <button
                  onClick={() => setReservationFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    reservationFilter === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-secondary-700 hover:bg-secondary-50 border border-secondary-200'
                  }`}
                >
                  All
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-white/20">
                    {reservations.length}
                  </span>
                </button>
              </div>
            )}

            {reservationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner spinner-lg text-primary-600"></div>
              </div>
            ) : reservations.length === 0 ? (
              <div className="card p-12">
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">No reservations yet</h3>
                  <p className="empty-state-description">
                    Once buyers reserve units from your projects, they will appear here.
                  </p>
                </div>
              </div>
            ) : filteredReservations.length === 0 ? (
              <div className="card p-12">
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h3 className="empty-state-title">No active reservations</h3>
                  <p className="empty-state-description">
                    All reservations have been completed or expired.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReservations.map((reservation) => {
                  const contractStatus = getContractStatus(reservation)
                  return (
                  <div key={reservation.id} className="card p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-secondary-900">
                            {reservation.unit?.project?.title} - Unit {reservation.unit?.unit_number}
                          </h3>
                          <span className={`badge text-sm font-semibold ${
                            reservation.status === 'active' ? 'badge-success' :
                            reservation.status === 'withdrawn' ? 'bg-orange-100 text-orange-800' :
                            reservation.status === 'expired' ? 'badge-error' :
                            'badge-primary'
                          }`}>
                            {reservation.status === 'withdrawn' ? 'WITHDRAWN' :
                             reservation.status === 'expired' ? 'EXPIRED' :
                             reservation.status.toUpperCase()}
                          </span>
                        </div>

                        {/* Contract Status Section */}
                        <div className="mb-4 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                          <div className="flex items-start gap-3">
                            <div className="text-2xl mt-1">{contractStatus.icon}</div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-secondary-900">Contract Status:</h4>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${contractStatus.color}`}>
                                  {contractStatus.label}
                                </span>
                              </div>
                              <p className="text-sm text-secondary-700 mb-2">{contractStatus.description}</p>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                                <p className="text-sm font-medium text-primary-700">
                                  <span className="font-semibold">Next Step:</span> {contractStatus.nextStep}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-secondary-600 mb-3">
                          <div>
                            <span className="font-medium">Buyer:</span> {reservation.buyer?.full_name}
                          </div>
                          <div>
                            <span className="font-medium">Email:</span> {reservation.buyer?.email}
                          </div>
                          <div>
                            <span className="font-medium">Phone:</span> {reservation.buyer?.phone || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Reserved:</span>{' '}
                            {new Date(reservation.created_at).toLocaleDateString('en-NZ')}
                          </div>
                        </div>

                        {reservation.status === 'active' && (
                          <p className="text-sm text-secondary-600 mb-3">
                            <span className="font-medium">Expires:</span>{' '}
                            {new Date(reservation.expires_at).toLocaleString('en-NZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}

                        {(reservation.status === 'withdrawn' || reservation.status === 'expired') && reservation.status_changed_at && (
                          <p className="text-sm font-medium text-secondary-700 mb-3">
                            <span className="font-medium">
                              {reservation.status === 'withdrawn' ? 'Withdrawn' : 'Expired'}:
                            </span>{' '}
                            {new Date(reservation.status_changed_at).toLocaleDateString('en-NZ', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })} at{' '}
                            {new Date(reservation.status_changed_at).toLocaleTimeString('en-NZ', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}

                        {reservation.notes && (
                          <div className="mt-3 p-3 bg-secondary-50 rounded-lg">
                            <p className="text-sm font-medium text-secondary-900 mb-1">Buyer Notes:</p>
                            <p className="text-sm text-secondary-700">{reservation.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-4 border-t border-secondary-200">
                      <a
                        href={`mailto:${reservation.buyer?.email}`}
                        className="btn-primary"
                      >
                        Contact Buyer
                      </a>
                      <Link
                        to={`/projects/${reservation.unit?.project?.id}`}
                        className="btn-outline"
                      >
                        View Project
                      </Link>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
